/**
 * Shared presentational primitives for the DLC_01 (ZE) + DLC_02 features.
 * Matches the app's industrial/mono aesthetic: sharp corners, font-mono uppercase
 * labels, hard red accent, .card surfaces, the shared Badge/Button kit.
 */
import React from "react";
import { clsx } from "clsx";
import { ChevronDown, ChevronRight, LayoutGrid, Rows3 } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { useAuthStore, type AuthUser } from "@/store/auth";
import { useManagedFileOpener } from "@/hooks/useManagedFileOpener";
import { isPlainLeftClick } from "@/utils/managedFile";
import { Badge } from "@/components/ui/Badge";
import { FileDropZone } from "@/components/ui/FileDropZone";
import type {
  ApplicationStatus,
  DeadlineStatus,
  DispatchStatus,
  EdeliveryBrief,
  KpiActionStatus,
  KpiReviewStatus,
  MilestoneState,
  OwnerAccessStatus,
  PaperDispatchBrief,
  PrintOrderBrief,
  PrintOrderStatus,
  Parcel,
  ParcelStatus,
  ProceedingOutcome,
  ProceedingScope,
  ProceedingStatus,
  RegisterDirection,
  TopicStatus,
} from "../types";
import { fmtDate, fmtDateTime } from "../utils";

export type BadgeColor =
  | "gray" | "blue" | "green" | "red" | "yellow" | "purple" | "orange" | "black";

// ── page header ────────────────────────────────────────────────────────────
/* One-line intro + actions for a tab section that used to be its own page
   (e.g. the two presets tabs) — the page above already carries the PageHeader. */
export function SectionToolbar({
  text,
  actions,
}: {
  text: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-xs text-[var(--color-text-secondary)]">{text}</p>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  backTo,
  backLabel,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  backTo?: string;
  backLabel?: string;
}) {
  return (
    <div className="mb-5">
      {backTo && (
        <Link
          to={backTo}
          className="section-label inline-flex items-center gap-1 hover:text-[var(--color-primary)] transition-colors"
        >
          ← {backLabel}
        </Link>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3 mt-1">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold leading-tight text-[var(--color-text-primary)]">
            {title}
          </h1>
          <div className="h-[3px] w-12 bg-[var(--color-primary)] mt-2" />
          {subtitle && <p className="section-label mt-2">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

// ── stat tile (header KPIs) ──────────────────────────────────────────────────
export function StatTile({
  label,
  value,
  tone = "default",
  hint,
  onClick,
  title,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  tone?: "default" | "danger" | "ok" | "warn" | "blue";
  hint?: React.ReactNode;
  // Klikalny kafelek (np. odsłonięcie kwoty) — renderuje się jako <button>, żeby
  // działała klawiatura i focus; bez onClick zostaje zwykłym <div>.
  onClick?: () => void;
  title?: string;
}) {
  const toneCls =
    tone === "danger"
      ? "text-[var(--color-primary)]"
      : tone === "ok"
        ? "text-emerald-600"
        : tone === "warn"
          ? "text-amber-600"
          : tone === "blue"
            ? "text-blue-600"
            : "text-[var(--color-text-primary)]";
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      {...(onClick ? { type: "button" as const, onClick } : {})}
      title={title}
      className={clsx(
        "card flex flex-col justify-between px-3 py-2 min-w-[96px]",
        onClick && "text-left hover:border-[var(--color-primary)] transition-colors",
      )}
    >
      <p className="section-label">{label}</p>
      <p className={clsx("font-mono text-lg font-bold leading-tight mt-1", toneCls)}>
        {value}
      </p>
      {/* Always render the hint row so tiles with/without a hint stay equal height. */}
      <p className="text-[10px] font-mono text-[var(--color-text-muted)] leading-tight mt-0.5 min-h-[0.875rem]">
        {hint || " "}
      </p>
    </Wrapper>
  );
}

// ── card with header ─────────────────────────────────────────────────────────
export function SectionCard({
  title,
  action,
  children,
  className,
  bodyClassName,
  collapsible = false,
  defaultCollapsed = false,
}: {
  title?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  /** Header click folds the body. The body stays mounted (only hidden), so a
   *  half-filled form inside survives collapsing. */
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}) {
  const [collapsedState, setCollapsed] = React.useState(defaultCollapsed);
  const collapsed = collapsible && collapsedState;
  const toggle = () => setCollapsed((c) => !c);
  return (
    <section className={clsx("card", className)}>
      {(title || action) && (
        <div
          className={clsx(
            "flex items-center justify-between gap-2 px-4 py-2.5 bg-[var(--color-bg-elevated)]",
            !collapsed && "border-b border-[var(--color-border)]",
            collapsible && "cursor-pointer select-none",
          )}
          onClick={collapsible ? toggle : undefined}
        >
          <h2
            className={clsx(
              "font-mono text-[0.7rem] font-bold uppercase tracking-widest text-[var(--color-text-secondary)]",
              collapsible && "flex items-center gap-1.5",
            )}
          >
            {collapsible && (
              <button
                type="button"
                aria-expanded={!collapsed}
                aria-label={collapsed ? "Rozwiń sekcję" : "Zwiń sekcję"}
                title={collapsed ? "Rozwiń sekcję" : "Zwiń sekcję"}
                className="shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle();
                }}
              >
                {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              </button>
            )}
            {title}
          </h2>
          {collapsible && action ? (
            // Actions in the header must not fold the card on click.
            <div onClick={(e) => e.stopPropagation()}>{action}</div>
          ) : (
            action
          )}
        </div>
      )}
      <div className={clsx("p-4", bodyClassName)} hidden={collapsed}>
        {children}
      </div>
    </section>
  );
}

export function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="py-10 text-center section-label tracking-wide">
      {children}
    </div>
  );
}

// ── progress bar (sharp, red fill) ───────────────────────────────────────────
export function Progress({
  pct,
  className,
}: {
  pct: number;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        "h-1.5 w-full bg-[var(--color-bg-elevated)] border border-[var(--color-border)] overflow-hidden",
        className,
      )}
    >
      <div
        className="h-full bg-[var(--color-primary)] transition-[width] duration-300"
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
    </div>
  );
}

// ── status badges ────────────────────────────────────────────────────────────
// Dyrektorskie oznaczenie tematu (portfel + nagłówek workspace). Edytowalne tylko
// przez CEO / dyrektora technicznego (TopicStatusControl), dla reszty view-only.
export const TOPIC_STATUS: Record<TopicStatus, { color: BadgeColor; dot: string; label: string }> = {
  in_progress: { color: "green", dot: "#059669", label: "Realizacja" },
  shelved: { color: "yellow", dot: "#d97706", label: "Półka" },
  finishing: { color: "blue", dot: "#2563eb", label: "Kończenie" },
  closed: { color: "black", dot: "#9ca3af", label: "Zamknięty" },
};
export const TOPIC_STATUS_ORDER: TopicStatus[] = ["in_progress", "shelved", "finishing", "closed"];
export function TopicStatusBadge({
  status,
  className,
  plain = false,
}: {
  status: TopicStatus;
  className?: string;
  /** Bez ramki Badge — do osadzenia w chipie nagłówka, który sam niesie kolor statusu. */
  plain?: boolean;
}) {
  const s = TOPIC_STATUS[status] ?? TOPIC_STATUS.in_progress;
  if (plain) {
    return (
      <span className={clsx("inline-flex items-center font-mono text-xs font-bold uppercase tracking-wide", className)}>
        <span className="w-1.5 h-1.5 rounded-full mr-1.5 shrink-0" style={{ background: s.dot }} />
        {s.label}
      </span>
    );
  }
  return (
    <Badge color={s.color} className={className}>
      <span className="w-1.5 h-1.5 rounded-full mr-1.5 shrink-0" style={{ background: s.dot }} />
      {s.label}
    </Badge>
  );
}

const PARCEL: Record<ParcelStatus, { color: BadgeColor; label: string }> = {
  not_started: { color: "gray", label: "Nie rozpoczęto" },
  in_progress: { color: "blue", label: "W trakcie" },
  action_required: { color: "orange", label: "W trakcie – wymagane działanie" },
  application_sent: { color: "yellow", label: "Wniosek wysłany" },
  negotiator_sent: { color: "blue", label: "Negocjator wysłany" },
  ok: { color: "green", label: "Uzgodniona" },
  problem: { color: "red", label: "Problem" },
};
export function ParcelStatusBadge({ status }: { status: ParcelStatus }) {
  const s = PARCEL[status] ?? PARCEL.not_started;
  return <Badge color={s.color}>{s.label}</Badge>;
}

/** Gruby czerwony „!" przy statusie działki: są na niej NIEZAKOŃCZONE sprawy. Status
 *  liczy się z tytułów dostępu, więc działka bywa „Uzgodniona" mimo wiszącego
 *  postępowania (np. ULICP) — ten znak nie pozwala tego przeoczyć. */
export function OpenCasesMark({ cases }: { cases?: { id: string; label: string }[] }) {
  if (!cases || cases.length === 0) return null;
  const title =
    `Niezakończone sprawy (${cases.length}):\n` + cases.map((c) => `• ${c.label}`).join("\n");
  return (
    <span
      className="text-red-600 font-black text-[15px] leading-none shrink-0 cursor-help"
      title={title}
      aria-label={title}
    >
      !
    </span>
  );
}
export function parcelDotColor(status: ParcelStatus): string {
  return (
    {
      not_started: "#9ca3af",
      in_progress: "#2563eb",
      action_required: "#ea580c",
      application_sent: "#d97706",
      negotiator_sent: "#2563eb",
      ok: "#047857",
      problem: "#dc1f1f",
    } as Record<ParcelStatus, string>
  )[status];
}

// Wypełnienie działki na mapie — JEDNO źródło dla mapy głównej, mini-mapy działki
// i edytora ramek formatki:
//  - „na zapas" (is_used=false: trasa nie przebiega, nie stoi obiekt — sąsiad zamówiony
//    w wypisie z ringu) — piaskowy. Wcześniej szła po kolorze statusu, a że dostaje tryb
//    „niewymagane" → status OK, malowała się tym samym zielonym co działki UZGODNIONE;
//  - powiadomienie (demontaż) — jasna zieleń niezależnie od statusu;
//  - reszta — kolor statusu (parcelDotColor).
export const PARCEL_SPARE_FILL = "#fde68a";
export const PARCEL_NOTIFICATION_FILL = "#86efac";

// „Czym jest ta działka na mapie" — kubełek decydujący o wypełnieniu. Wydzielony, bo
// legenda pod mapą grupuje działki po TYM SAMYM kluczu: dopóki obie strony pytają o niego
// tutaj, próbka w legendzie nie może zacząć kłamać o kolorze, którym mapa maluje wielokąt.
export type ParcelFillKind = "spare" | "notification" | ParcelStatus;
export function parcelFillKind(
  p: Pick<Parcel, "is_used" | "access_mode" | "status">,
): ParcelFillKind {
  if (!p.is_used) return "spare";
  if (p.access_mode === "notification") return "notification";
  return p.status;
}
export function parcelFillColor(p: Pick<Parcel, "is_used" | "access_mode" | "status">): string {
  const kind = parcelFillKind(p);
  if (kind === "spare") return PARCEL_SPARE_FILL;
  if (kind === "notification") return PARCEL_NOTIFICATION_FILL;
  return parcelDotColor(kind);
}

/**
 * JEDNO źródło prawdy o stanach kamienia milowego — używa go tablica kamieni
 * (siatka + kanban), modal szczegółów ORAZ graf procesu (schemat blokowy). Wcześniej
 * każdy z tych widoków miał własną mapę etykiet i kolorów, więc ten sam kamień czytał
 * się inaczej w zależności od zakładki („W trakcie" vs „w toku", inne kolory kafelka).
 *   label  — etykieta w UI (jedna, wszędzie ta sama)
 *   hint   — dopowiedzenie w podpowiedzi / na liście wyboru („wysłane / złożono")
 *   accent — kolor akcentu (belka karty, kropka, obramowanie kolumny kanbana)
 *   tint   — delikatna poświata tła karty w siatce
 *   bg/fg  — wypełnienie i tekst kafelka na grafie procesu
 */
const MILESTONE: Record<
  MilestoneState,
  { color: BadgeColor; label: string; hint: string; accent: string; tint: string; bg: string; fg: string }
> = {
  not_started: {
    color: "gray", label: "Nie rozpoczęto", hint: "krok jeszcze nieruszony",
    accent: "var(--color-border-strong)", tint: "transparent", bg: "#f8fafc", fg: "#64748b",
  },
  in_progress: {
    color: "blue", label: "W trakcie", hint: "robimy — po naszej stronie",
    accent: "#2563eb", tint: "rgba(37, 99, 235, 0.05)", bg: "#dbeafe", fg: "#1e40af",
  },
  submitted: {
    color: "yellow", label: "Wysłane", hint: "złożone — czekamy na stronę zewnętrzną",
    accent: "#d97706", tint: "rgba(217, 119, 6, 0.06)", bg: "#fef3c7", fg: "#92400e",
  },
  done: {
    color: "green", label: "Gotowe", hint: "uzgodnione / zamknięte",
    accent: "#059669", tint: "rgba(5, 150, 105, 0.06)", bg: "#dcfce7", fg: "#166534",
  },
  applicable: {
    color: "purple", label: "Dotyczy", hint: "krok dotyczy projektu, jeszcze nieruszony",
    accent: "#7c3aed", tint: "rgba(124, 58, 237, 0.05)", bg: "#ede9fe", fg: "#5b21b6",
  },
  na: {
    color: "black", label: "Nie dotyczy", hint: "krok pominięty w tym projekcie",
    accent: "var(--color-border)", tint: "transparent", bg: "#0a0a0a", fg: "#f5f5f5",
  },
};
export function milestoneMeta(state: MilestoneState) {
  return MILESTONE[state] ?? MILESTONE.not_started;
}
// Kolejność stanów: kolumny kanbana, przyciski w modalu i lista wyboru na grafie.
// Para „dotyczy / nie dotyczy" stoi na końcu — jest poza tokiem postępu.
export const MILESTONE_STATES: MilestoneState[] = [
  "not_started", "in_progress", "submitted", "done", "applicable", "na",
];
// „Jak bardzo domknięty" — lustro backendowego MILESTONE_STATE_RANK. Spadek rangi to
// cofnięcie kroku i wymaga powodu (tak samo z tablicy, jak i z grafu).
export const MILESTONE_STATE_RANK: Record<MilestoneState, number> = {
  not_started: 0, applicable: 0, in_progress: 1, submitted: 2, done: 3, na: 3,
};

// "Nie dotyczy" milestones black out entirely on the board/grid: a solid black tile
// with light text. We locally remap the surface + text/border CSS vars on the card,
// so every nested label (title, dates, footer) inverts to light automatically —
// no per-element class changes needed. Callers still own the borderLeft shorthand.
export const MILESTONE_NA_STYLE = {
  background: "#0a0a0a",
  "--color-text-primary": "#f5f5f5",
  "--color-text-secondary": "#e5e5e5",
  "--color-text-muted": "#a3a3a3",
  "--color-border": "#404040",
  "--color-border-muted": "#404040",
} as React.CSSProperties;

// ── per-owner access title (OwnerAccess) ─────────────────────────────────────
const OWNER_ACCESS: Record<OwnerAccessStatus, { color: BadgeColor; label: string }> = {
  not_started: { color: "gray", label: "Nie rozpoczęto" },
  in_progress: { color: "blue", label: "W trakcie" },
  secured: { color: "green", label: "Pozyskany" },
  refused: { color: "red", label: "Odmowa" },
  not_required: { color: "gray", label: "Niewymagany" },
  problem: { color: "red", label: "Problem (teren)" },
};
export function OwnerAccessBadge({
  status,
  isStale,
}: {
  status: OwnerAccessStatus;
  isStale?: boolean;
}) {
  const s = OWNER_ACCESS[status] ?? OWNER_ACCESS.not_started;
  // SECURED on a non-current route version reads as a warning (not green "pozyskany").
  const color = isStale ? "yellow" : s.color;
  const label = isStale ? "Pozyskany (nieaktualna wersja)" : s.label;
  return <Badge color={color}>{label}</Badge>;
}

// Ten sam stan tytułu dostępu, ale jako pionowy pasek przy nazwisku (kolumna „Właściciel"
// na liście działek) — na liście nie ma miejsca na plakietkę przy każdym współwłaścicielu,
// a kolor musi się czytać jednym rzutem oka. Kolory idą za paletą Badge z OWNER_ACCESS,
// żeby pasek i plakietka w szufladzie działki mówiły to samo.
const OWNER_ACCESS_BAR: Record<OwnerAccessStatus, string> = {
  not_started: "#9ca3af", // szary — nikt jeszcze nie ruszył tematu
  in_progress: "#2563eb", // niebieski — w toku (np. negocjator w terenie)
  secured: "#059669", // zielony — pozyskany
  refused: "#dc2626", // czerwony — odmowa
  not_required: "#64748b", // stalowy — tytuł niewymagany
  problem: "#dc2626", // czerwony — problem w terenie
};

/** Kolor + opis paska statusu jednego właściciela. `access` = null → brak tytułu dostępu
 *  (właściciel dodany, ale nic z nim nie ruszone). `inField` dopisuje kontekst WPW. */
export function ownerAccessMark(
  access: { status: OwnerAccessStatus; is_stale?: boolean } | null | undefined,
  inField?: boolean,
  inOpenCase?: boolean,
  inOpenOfficeCase?: boolean,
): { color: string; label: string } {
  const field = inField ? " · negocjator w terenie" : "";
  // „Problem" (zgon, niemożliwe zebranie podpisu) objęty otwartą sprawą URZĘDOWĄ czyta się
  // jako „W trakcie" — automat liczy działkę po wniosku w urzędzie. Pasek mówi to samo co
  // karta właściciela i kolumna „Podstawa dostępu" tabeli statusów.
  if (access?.status === "problem" && inOpenOfficeCase) {
    return {
      color: OWNER_ACCESS_BAR.in_progress,
      label: `W trakcie (wniosek w urzędzie)${field}`,
    };
  }
  // „Nie rozpoczęto" (albo brak tytułu) czyta się jako „W trakcie", gdy właściciela obejmuje
  // otwarta sprawa albo negocjator ruszył w terenie — tak samo jak na karcie właściciela
  // i w kolumnie „Podstawa dostępu" tabeli statusów.
  if ((!access || access.status === "not_started") && (inOpenCase || inField)) {
    return {
      color: OWNER_ACCESS_BAR.in_progress,
      label: inOpenCase ? "W trakcie (otwarta sprawa)" : "W trakcie (negocjator w terenie)",
    };
  }
  if (!access) return { color: "#9ca3af", label: `Brak tytułu dostępu${field}` };
  const s = OWNER_ACCESS[access.status] ?? OWNER_ACCESS.not_started;
  // SECURED na nieaktualnej wersji trasy nie jest „zielony" — tak samo jak w plakietce.
  if (access.is_stale) return { color: "#d97706", label: `Pozyskany (nieaktualna wersja trasy)${field}` };
  return { color: OWNER_ACCESS_BAR[access.status] ?? OWNER_ACCESS_BAR.not_started, label: `${s.label}${field}` };
}

// Small inline "nieaktualna wersja trasy" warning chip.
export function StaleBadge() {
  return <Badge color="orange">Nieaktualna wersja trasy</Badge>;
}

// Pełny log zadania w tle (AI lookupy itp.): przewijany, doklejony do dołu,
// rośnie razem ze strumieniem — zamiast ucinania do ostatnich 3 linii.
export function TaskLog({ log }: { log: string }) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log]);
  const text = (log ?? "").split("\n").filter(Boolean).join("\n");
  if (!text) return null;
  return (
    <div
      ref={ref}
      className="max-h-40 overflow-y-auto border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-1.5"
    >
      <pre className="whitespace-pre-wrap font-mono text-[10px] leading-tight">{text}</pre>
    </div>
  );
}

// ── proceeding (correspondence thread) ───────────────────────────────────────
const PROCEEDING_STATUS: Record<ProceedingStatus, { color: BadgeColor; label: string }> = {
  open: { color: "yellow", label: "Otwarte" },
  closed: { color: "gray", label: "Zamknięte" },
};
export function ProceedingStatusBadge({ status }: { status: ProceedingStatus }) {
  const s = PROCEEDING_STATUS[status] ?? PROCEEDING_STATUS.open;
  return <Badge color={s.color}>{s.label}</Badge>;
}

const PROCEEDING_SCOPE: Record<ProceedingScope, { color: BadgeColor; label: string }> = {
  owner: { color: "blue", label: "Właściciel" },
  office: { color: "purple", label: "Urzędowe" },
  technical: { color: "gray", label: "Techniczne" },
  notification: { color: "yellow", label: "Zawiadomienie" },
  letter: { color: "green", label: "List" },
};
export function ProceedingScopeBadge({ scope }: { scope: ProceedingScope }) {
  const s = PROCEEDING_SCOPE[scope] ?? PROCEEDING_SCOPE.owner;
  return <Badge color={s.color}>{s.label}</Badge>;
}

const OUTCOME: Record<ProceedingOutcome, { color: BadgeColor; label: string }> = {
  none: { color: "gray", label: "Bez rozstrzygnięcia" },
  positive: { color: "green", label: "Pozytywny" },
  negative: { color: "red", label: "Odmowny" },
  partial: { color: "orange", label: "Częściowy" },
};
export function OutcomeBadge({ outcome }: { outcome: ProceedingOutcome }) {
  const s = OUTCOME[outcome] ?? OUTCOME.none;
  return <Badge color={s.color}>{s.label}</Badge>;
}

// ── application lifecycle (DRAFT → APPROVED → SUBMITTED) ──────────────────────
const APPLICATION: Record<Exclude<ApplicationStatus, "">, { color: BadgeColor; label: string }> = {
  draft: { color: "gray", label: "Szkic" },
  approved: { color: "blue", label: "Zatwierdzony" },
  submitted: { color: "green", label: "Złożony" },
  completed: { color: "green", label: "Zakończony" },
  withdrawn: { color: "red", label: "Wycofany" },
};
export function ApplicationStatusBadge({ status }: { status: ApplicationStatus }) {
  if (!status) return null;
  const s = APPLICATION[status] ?? APPLICATION.draft;
  return <Badge color={s.color}>{s.label}</Badge>;
}

// ── KPA deadline (procedural clock) ──────────────────────────────────────────
export function KpaBadge({
  deadline,
  status,
  overdue,
}: {
  deadline: string | null;
  status: DeadlineStatus;
  overdue: boolean;
}) {
  if (!deadline) return null;
  const color: BadgeColor = overdue ? "red" : status === "soon" ? "orange" : status === "approaching" ? "yellow" : "gray";
  return (
    <span title={overdue ? "Przekroczony termin KPA" : "Termin KPA"}>
      <Badge color={color}>KPA: {fmtDate(deadline)}</Badge>
    </span>
  );
}

// ── register direction (DLC_02 dziennik nadawczo-odbiorczy) ──────────────────
export function RegisterDirectionBadge({ direction }: { direction: RegisterDirection }) {
  return direction === "incoming" ? (
    <Badge color="blue">Przychodzące ←</Badge>
  ) : (
    <Badge color="purple">Wychodzące →</Badge>
  );
}

const KPI_REVIEW: Record<KpiReviewStatus, { color: BadgeColor; label: string }> = {
  planned: { color: "blue", label: "Zaplanowane" },
  held: { color: "green", label: "Odbyte" },
  cancelled: { color: "gray", label: "Odwołane" },
};
export function KpiReviewBadge({ status }: { status: KpiReviewStatus }) {
  const s = KPI_REVIEW[status] ?? KPI_REVIEW.planned;
  return <Badge color={s.color}>{s.label}</Badge>;
}

// Kolory lustrzane do statusów Friday (TASK_STATUS_COLORS), żeby ten sam stan pracy
// wyglądał tak samo w obu miejscach.
const KPI_ACTION: Record<KpiActionStatus, { color: BadgeColor; label: string }> = {
  open: { color: "yellow", label: "Do zrobienia" },
  in_progress: { color: "blue", label: "W trakcie" },
  waiting: { color: "orange", label: "Wstrzymane" },
  done: { color: "green", label: "Zrobione" },
  cancelled: { color: "gray", label: "Anulowane" },
};

export const KPI_ACTION_LABELS = Object.fromEntries(
  Object.entries(KPI_ACTION).map(([k, v]) => [k, v.label]),
) as Record<KpiActionStatus, string>;

// Kolejność w selectach i w kolumnach — od „jeszcze nie" do „już nie".
export const KPI_ACTION_ORDER: KpiActionStatus[] = [
  "open", "in_progress", "waiting", "done", "cancelled",
];

export const KPI_ACTION_UNFINISHED: KpiActionStatus[] = ["open", "in_progress", "waiting"];

// Klasy tła/tekstu 1:1 z fridayowym TASK_STATUS_COLORS — ten sam stan pracy ma wyglądać
// tak samo w tabeli, w panelu szczegółów i wszędzie indziej.
export const STATUS_CELL: Record<KpiActionStatus, string> = {
  open: "bg-blue-100 text-blue-700",
  in_progress: "bg-violet-100 text-violet-700",
  waiting: "bg-yellow-100 text-yellow-700",
  done: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-600",
};

export const isKpiUnfinished = (status: KpiActionStatus) =>
  KPI_ACTION_UNFINISHED.includes(status);
export function KpiActionBadge({ status }: { status: KpiActionStatus }) {
  const s = KPI_ACTION[status] ?? KPI_ACTION.open;
  return <Badge color={s.color}>{s.label}</Badge>;
}

const DISPATCH: Record<DispatchStatus, { color: BadgeColor; label: string }> = {
  queued: { color: "gray", label: "W kolejce" },
  prepared: { color: "blue", label: "Przygotowane" },
  sent: { color: "yellow", label: "Wysłane" },
  delivered: { color: "green", label: "Doręczone" },
  returned: { color: "orange", label: "Zwrot" },
  undeliverable: { color: "red", label: "Niedoręczalne" },
  received: { color: "blue", label: "Zarejestrowane (wpływ)" },
  filed: { color: "yellow", label: "Zadekretowane" },
  acknowledged: { color: "green", label: "Potwierdzono odbiór" },
  // Autor wycofał pismo z kolejki wysyłki — wpis zostaje w dzienniku, papier nie wychodzi.
  cancelled: { color: "gray", label: "Anulowane" },
};
export function DispatchBadge({ status }: { status: DispatchStatus }) {
  const s = DISPATCH[status] ?? DISPATCH.queued;
  return <Badge color={s.color}>{s.label}</Badge>;
}
export const DISPATCH_LABEL = Object.fromEntries(
  Object.entries(DISPATCH).map(([k, v]) => [k, v.label]),
) as Record<DispatchStatus, string>;

/**
 * „Przegląd zleconej wysyłki" — stan przesyłki papierowej w firmowym dzienniku korespondencji
 * (DLC_02) pokazany przy piśmie, w module, z którego pismo poszło. Autor wniosku nie widzi
 * samego dziennika (wpis wychodzący nie jest na niego dekretowany), więc bez tej linijki po
 * złożeniu wniosku ma tylko etykietę kanału „Papier (DLC_02)" i nie wie, czy przesyłka czeka
 * w kolejce, czy już wyszła — stąd zakładane „na wszelki wypadek" dublujące zlecenia wydruku.
 * Dopóki przesyłka czeka w kolejce, mówimy to wprost.
 */
// Przesyłka e-Doręczeń (DLC_09) pisma: do akceptacji CEO / dyrektora technicznego pismo
// jest złożone, nie wysłane — sprawa nie pokazuje „wysłano" i nie liczy terminu KPA.
export function EdeliveryLine({ edelivery }: { edelivery: EdeliveryBrief | null | undefined }) {
  if (!edelivery) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-mono text-[var(--color-text-secondary)]">
      <span className="text-[var(--color-text-muted)]">e-Doręczenia (DLC_09):</span>
      <Badge color={edelivery.awaiting_approval ? "yellow" : "green"}>{edelivery.status_display}</Badge>
      {edelivery.approved_at && <span>· zaakceptowano {fmtDateTime(edelivery.approved_at)}</span>}
      {edelivery.sent_at && <span>· wysłano {fmtDateTime(edelivery.sent_at)}</span>}
      {edelivery.awaiting_approval && edelivery.status !== "cancelled" && edelivery.status !== "withdrawn" && (
        <span className="text-[var(--color-text-muted)]">
          · pismo złożone, czeka na akceptację (CEO / dyrektor techniczny) — jeszcze nie wysłane
        </span>
      )}
    </div>
  );
}

export function PaperDispatchLine({ dispatch }: { dispatch: PaperDispatchBrief | null | undefined }) {
  const user = useAuthStore((s) => s.user);
  if (!dispatch) return null;
  const waiting = dispatch.status === "queued" || dispatch.status === "prepared";
  // Link only for the entry's AUTHOR with DLC_02 access — it targets the authored-only
  // "Moje pisma do wysłania" tab, where anybody else would not find this entry.
  const canOpen =
    !!user
    && user.id === dispatch.registered_by
    && (user.accessible_dlc_modules ?? []).includes("dlc_02");
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-mono text-[var(--color-text-secondary)]">
      <span className="text-[var(--color-text-muted)]">Wysyłka papierowa (dziennik DLC_02):</span>
      <DispatchBadge status={dispatch.status} />
      {dispatch.counterparty_name && <span>{dispatch.counterparty_name}</span>}
      {dispatch.date && <span>· nadano {fmtDate(dispatch.date)}</span>}
      {dispatch.tracking_number && <span>· nr {dispatch.tracking_number}</span>}
      {dispatch.handled_by_name && <span>· obsługa: {dispatch.handled_by_name}</span>}
      {waiting && (
        <span className="text-[var(--color-text-muted)]">
          · czeka w kolejce działu obiegu dokumentów — nie zakładaj osobnego zlecenia wydruku
        </span>
      )}
      {dispatch.status === "cancelled" && (
        <span className="text-[var(--color-text-muted)]">
          · wycofane z kolejki wysyłki — pismo NIE zostało wysłane
        </span>
      )}
      {canOpen && (
        <Link
          className="text-[var(--color-primary)] hover:underline"
          to={`/paper-send/print-orders?view=dispatch&entry=${dispatch.id}`}
        >
          otwórz w Moich pismach do wysłania →
        </Link>
      )}
    </div>
  );
}

const PRINT_ORDER: Record<PrintOrderStatus, { color: BadgeColor; label: string }> = {
  pending: { color: "gray", label: "Oczekuje na akceptację" },
  accepted: { color: "blue", label: "Do wydruku" },
  printed: { color: "blue", label: "Wydrukowane" },
  sent: { color: "yellow", label: "Wysłane" },
  returned: { color: "red", label: "Zwrot" },
  rejected: { color: "red", label: "Odrzucone" },
  cancelled: { color: "gray", label: "Anulowane" },
};

/** Stan ZLECENIA WYDRUKU (DLC_02) podpiętego pod list do właściciela — odpowiednik
 * PaperDispatchLine dla listów, które wyszły jako zlecenie wydruku z wysyłką. */
export function PrintOrderLine({ order }: { order: PrintOrderBrief | null | undefined }) {
  const user = useAuthStore((s) => s.user);
  if (!order) return null;
  const s = PRINT_ORDER[order.status] ?? PRINT_ORDER.sent;
  const canOpen = !!user && (user.accessible_dlc_modules ?? []).includes("dlc_02");
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-mono text-[var(--color-text-secondary)]">
      <span className="text-[var(--color-text-muted)]">Zlecenie wydruku (DLC_02):</span>
      <Badge color={s.color}>{s.label}</Badge>
      <span>#{order.number} {order.name}</span>
      {order.date && <span>· nadano {fmtDate(order.date)}</span>}
      {order.tracking_number && <span>· nr {order.tracking_number}</span>}
      {order.receipt_confirmation ? (
        <span className="text-emerald-600">· potwierdzenie odbioru w Dzienniku</span>
      ) : (
        <span className="text-[var(--color-text-muted)]">· bez potwierdzenia odbioru</span>
      )}
      {canOpen && (
        <Link
          className="text-[var(--color-primary)] hover:underline"
          to={`/paper-send/print-orders?entry=${order.id}`}
        >
          otwórz w Zleceniach wydruku →
        </Link>
      )}
    </div>
  );
}

const DEADLINE: Record<DeadlineStatus, { color: BadgeColor; label: string }> = {
  overdue: { color: "red", label: "Po terminie" },
  soon: { color: "red", label: "≤ 14 dni" },
  approaching: { color: "yellow", label: "≤ 30 dni" },
  ok: { color: "green", label: "W terminie" },
  none: { color: "gray", label: "Brak terminu" },
};
export function DeadlineBadge({
  status,
  date,
}: {
  status: DeadlineStatus;
  date: string | null;
}) {
  const s = DEADLINE[status] ?? DEADLINE.none;
  return (
    <span title={s.label}>
      <Badge color={s.color}>{date ? fmtDate(date) : "—"}</Badge>
    </span>
  );
}
// Map a deadline status to a StatTile tone, so the header deadline reads as a
// regular KPI tile (date coloured by urgency) instead of a boxed badge.
export function deadlineTone(
  status: DeadlineStatus,
): "default" | "danger" | "ok" | "warn" {
  if (status === "overdue" || status === "soon") return "danger";
  if (status === "approaching") return "warn";
  if (status === "ok") return "ok";
  return "default";
}
export function deadlineLabel(status: DeadlineStatus): string {
  return (DEADLINE[status] ?? DEADLINE.none).label;
}

// ── tabs ─────────────────────────────────────────────────────────────────────
export interface TabItem<T extends string> {
  key: T;
  label: string;
  count?: number;
  countLabel?: string;
  countTone?: "danger" | "warn" | "blue";
  /** Optional glyph rendered before the label (lucide icon element). */
  icon?: React.ReactNode;
}

/** A named cluster of tabs plus the colour that identifies it. Tone must be a HEX
 *  literal — the rail, the tint and the hover wash are derived from it. */
export interface TabGroup<T extends string> {
  label: string;
  tone: string;
  keys: T[];
}

/** #rrggbb → rgba(), so one group colour yields its rail, its tint and its hover
 *  wash without hand-maintaining three constants per group. */
function withAlpha(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** A per-browser preference. Deliberately NOT a server-side user setting: this is
 *  a view habit, it costs nothing to change, and nobody should wait for a round
 *  trip (or a migration) to try it. */
export function usePersistedFlag(storageKey: string, initial: boolean) {
  const [value, setValue] = React.useState<boolean>(() => {
    if (!storageKey) return initial;
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw == null ? initial : raw === "1";
    } catch {
      return initial; // private mode / blocked storage — the toggle still works, just per session
    }
  });
  const set = React.useCallback(
    (next: boolean) => {
      setValue(next);
      try {
        if (storageKey) window.localStorage.setItem(storageKey, next ? "1" : "0");
      } catch {
        /* ignore */
      }
    },
    [storageKey],
  );
  return [value, set] as const;
}

function TabChip<T extends string>({
  tab,
  active,
  tone,
  variant,
  onChange,
}: {
  tab: TabItem<T>;
  active: boolean;
  tone: string;
  /** "underline" = classic flat bar; "solid" = filled chip inside a group row. */
  variant: "underline" | "solid";
  onChange: (k: T) => void;
}) {
  const [hover, setHover] = React.useState(false);
  const solid = variant === "solid";
  const style: React.CSSProperties = solid
    ? active
      ? { backgroundColor: tone, color: "#fff" }
      : hover
        ? { backgroundColor: withAlpha(tone, 0.12), color: tone }
        : {}
    : active
      ? { color: tone, borderBottomColor: tone }
      : {};
  return (
    <button
      onClick={() => onChange(tab.key)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={style}
      className={clsx(
        "font-mono font-bold uppercase tracking-wider whitespace-nowrap transition-colors inline-flex items-center gap-1.5",
        solid ? "px-2.5 py-1.5 text-[11px]" : "px-3 py-2 text-xs border-b-2 -mb-px",
        !active && "text-[var(--color-text-muted)]",
        !solid && !active && "border-transparent hover:text-[var(--color-text-primary)]",
      )}
    >
      {tab.icon}
      {tab.label}
      {tab.count != null && tab.count > 0 && (
        <span
          // On a filled chip a red badge on red would vanish — invert it.
          style={active && solid ? { backgroundColor: "#fff", color: tone } : undefined}
          className={clsx(
            "text-[10px] font-mono px-1.5 py-0.5 leading-none",
            active && solid
              ? ""
              : clsx(
                  "text-white",
                  tab.countTone === "danger"
                    ? "bg-[var(--color-primary)]"
                    : tab.countTone === "blue"
                      ? "bg-blue-600"
                      : "bg-amber-500",
                ),
          )}
        >
          {tab.countLabel ?? tab.count}
        </span>
      )}
    </button>
  );
}

function GroupingToggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={
        on
          ? "Zakładki pogrupowane — kliknij, aby wrócić do jednego paska"
          : "Pogrupuj zakładki (prowadzenie / teren / urzędy / dokumenty / zespół)"
      }
      className={clsx(
        "ml-auto self-center mr-1 inline-flex items-center gap-1 border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors",
        "border-[var(--color-border)] text-[var(--color-text-muted)]",
        "hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]",
      )}
    >
      {on ? <Rows3 size={12} /> : <LayoutGrid size={12} />}
      grupy
    </button>
  );
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
  groups,
  groupingStorageKey,
  sticky,
}: {
  tabs: TabItem<T>[];
  active: T;
  onChange: (k: T) => void;
  /** Optional clustering, offered as a per-browser opt-in (see groupingStorageKey).
   *  Grouped mode is a TWO-LEVEL nav: pick a group, see only its tabs. Twenty tabs
   *  in one wrapped row is a wall of text; five groups plus four-to-six tabs is a
   *  thing you can read. A tab missing from every group is NOT dropped — it lands
   *  in a trailing "Inne" group, so adding a tab can never make it vanish. */
  groups?: TabGroup<T>[];
  /** localStorage key holding the opt-in. Without it grouping is never offered. */
  groupingStorageKey?: string;
  /** Keep the bar visible while a long tab (e.g. Działki) scrolls under it. */
  sticky?: boolean;
}) {
  const [grouped, setGrouped] = usePersistedFlag(groupingStorageKey ?? "", false);
  const canGroup = !!groups?.length && !!groupingStorageKey;

  const byKey = new Map(tabs.map((t) => [t.key, t]));
  const clusters = (groups ?? [])
    .map((g) => ({
      ...g,
      items: g.keys.map((k) => byKey.get(k)).filter(Boolean) as TabItem<T>[],
    }))
    .filter((g) => g.items.length > 0);
  const claimed = new Set((groups ?? []).flatMap((g) => g.keys));
  const orphans = tabs.filter((t) => !claimed.has(t.key));
  if (clusters.length && orphans.length) {
    clusters.push({ label: "Inne", tone: "#888888", keys: [], items: orphans });
  }

  // Przypięty pasek parkuje na górze OBSZARU TREŚCI kontenera przewijania, a nie na
  // jego krawędzi — <main> ma `p-4 lg:p-6`, więc przy `top-0` między paskiem a górą
  // ekranu zostaje 16/24 px, przez które przejeżdża treść: wygląda to, jakby pasek
  // wchodził na wiersze. Podciągamy go o dokładnie tę samą wartość, żeby przykleił
  // się bez szpary i nic nad nim nie mrugało. Cień = granica „pasek / treść pod nim".
  const wrapper = clsx(
    "mb-5",
    sticky &&
      "sticky -top-4 lg:-top-6 z-20 -mx-1 bg-[var(--color-bg-base)] px-1 pt-1 " +
        "shadow-[0_6px_10px_-8px_rgba(0,0,0,0.35)]",
  );

  if (!canGroup || !grouped) {
    return (
      <div className={wrapper}>
        <div className="flex flex-wrap gap-0 border-b border-[var(--color-border)]">
          {tabs.map((t) => (
            <TabChip
              key={t.key}
              tab={t}
              active={active === t.key}
              tone="var(--color-primary)"
              variant="underline"
              onChange={onChange}
            />
          ))}
          {canGroup && <GroupingToggle on={false} onToggle={() => setGrouped(true)} />}
        </div>
      </div>
    );
  }

  // The open group follows the active tab — switching groups opens that group's
  // first tab, so a click never lands on a blank shelf.
  const openGroup = clusters.find((g) => g.items.some((t) => t.key === active)) ?? clusters[0];

  return (
    <div className={wrapper}>
      {/* One frame, two bands: the group tabs sit ON the shelf that holds their
          contents, so "which group am I in" needs no re-reading. */}
      <div className="border border-[var(--color-border)]">
        <div className="flex flex-wrap items-stretch bg-[var(--color-bg-elevated)]">
          {clusters.map((g) => {
            const isOpen = g.label === openGroup.label;
            // A dot, not a sum: the group says "something here", the tab row says
            // what exactly. A number would contradict badges like "1/12".
            const danger = g.items.some((t) => (t.count ?? 0) > 0 && t.countTone === "danger");
            const attention = g.items.some((t) => (t.count ?? 0) > 0);
            return (
              <button
                key={g.label}
                type="button"
                onClick={() => onChange(g.items[0].key)}
                title={g.items.map((t) => t.label).join(" · ")}
                // Even a closed group shows its colour (faint rail) — otherwise the
                // palette is only learnable by clicking through every group.
                style={
                  isOpen
                    ? { borderTopColor: g.tone, color: g.tone }
                    : { borderTopColor: withAlpha(g.tone, 0.45) }
                }
                className={clsx(
                  "inline-flex items-center gap-1.5 border-t-[3px] px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.16em] transition-colors",
                  isOpen
                    ? "bg-[var(--color-bg-surface)]"
                    : "text-[var(--color-text-muted)] hover:bg-[var(--color-bg-surface)] hover:text-[var(--color-text-primary)]",
                )}
              >
                {g.label}
                {attention && (
                  <span
                    className={clsx(
                      "inline-block h-1.5 w-1.5",
                      danger ? "bg-[var(--color-primary)]" : "bg-amber-500",
                    )}
                  />
                )}
              </button>
            );
          })}
          <GroupingToggle on onToggle={() => setGrouped(false)} />
        </div>

        <div
          className="flex flex-wrap items-stretch gap-0.5 border-t-2 py-1 pr-1 pl-2"
          style={{
            borderTopColor: openGroup.tone,
            backgroundColor: withAlpha(openGroup.tone, 0.05),
            boxShadow: `inset 3px 0 0 0 ${openGroup.tone}`,
          }}
        >
          {openGroup.items.map((t) => (
            <TabChip
              key={t.key}
              tab={t}
              active={active === t.key}
              tone={openGroup.tone}
              variant="solid"
              onChange={onChange}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Pill-style second-level tabs rendered inside a workspace tab (e.g. the merged
// Dokumentacja tab: Dokumentacja / Projekt tech. / Kosztorys).
export function SubTabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { key: T; label: string; count?: number; countTone?: "danger" | "warn" | "blue" }[];
  active: T;
  onChange: (k: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5 mb-4">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={clsx(
            "px-3 py-1.5 rounded-full font-mono text-[11px] font-bold uppercase tracking-wider whitespace-nowrap border transition-colors inline-flex items-center gap-1.5",
            active === t.key
              ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
              : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-text-muted)]",
          )}
        >
          {t.label}
          {t.count != null && t.count > 0 && (
            <span
              className={clsx(
                "text-[10px] font-mono rounded-full px-1.5 py-0.5 leading-none text-white",
                active === t.key
                  ? "bg-white/25"
                  : t.countTone === "danger"
                    ? "bg-[var(--color-primary)]"
                    : t.countTone === "blue"
                      ? "bg-blue-600"
                      : "bg-amber-500",
              )}
            >
              {t.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ── route-based hub nav (top-level DLC tabs) ─────────────────────────────────
// `gate` hides the tab unless the predicate passes (e.g. permission-gated tabs);
// `adminOnly` is a shorthand for director/CEO-only.
// DLC_01 (Projekty ZE) has NO hub nav: every view of the module lives in the
// left sidebar (Sidebar.tsx, section "Projekty ZE") — the horizontal bar used to
// duplicate part of it and hide the rest.
export type HubLink = {
  to: string;
  label: string;
  end?: boolean;
  adminOnly?: boolean;
  gate?: (user: AuthUser | null) => boolean;
};

// DLC_02 · Obieg dokumentów — dziennik nadawczo-odbiorczy + rejestr urzędów (odbiorców).
//
// Rejestr urzędów jest WSPÓLNY dla modułów i stoi poza bramką DLC_02 (zob. trasę w
// routes/index.tsx), więc trafia tu też ktoś, kto ma tylko DLC_01 — sam obieg dokumentów
// musi się wtedy schować, inaczej zakładki prowadziłyby pod bramkę i wyrzucały na pulpit.
const hasDLC02 = (u: AuthUser | null) => !!u?.accessible_dlc_modules?.includes("dlc_02");

export const DLC02_HUB_LINKS: HubLink[] = [
  // Firmowy dziennik (cała korespondencja) — tylko z uprawnieniem "Firmowy obieg dokumentów".
  { to: "/paper-send", label: "Dziennik korespondencji", end: true, gate: (u) => hasDLC02(u) && !!u?.has_correspondence_register_permission },
  { to: "/paper-send/mine", label: "Moja korespondencja", gate: hasDLC02 },
  { to: "/paper-send/print-orders", label: "Zlecenia wydruku", gate: hasDLC02 },
  { to: "/paper-send/offices", label: "Urzędy" },
];

/* A single dynamic count on a hub tab. `tone` maps to the badge colour; omit for the
   default primary chip. Several may sit next to one tab (e.g. "do potwierdzenia" +
   "do zadekretowania" on Moja korespondencja). */
export type HubBadge = { count: number; title?: string; tone?: "primary" | "amber" | "red" };

/* Small count chip for a PageHeader's `actions` slot — replaces the per-tab badge
   the DLC_01 hub nav used to show for the current view (e.g. "3 · do wysyłki"). */
export function HeaderCount({
  count,
  label,
  tone = "primary",
}: {
  count: number;
  label: string;
  tone?: HubBadge["tone"];
}) {
  if (count <= 0) return null;
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[11px] font-bold leading-none",
        tone === "amber"
          ? "bg-amber-500 text-white"
          : tone === "red"
            ? "bg-red-500 text-white"
            : "bg-[var(--color-primary)] text-[var(--color-text-inverse)]",
      )}
    >
      {count}
      <span className="font-normal opacity-90">· {label}</span>
    </span>
  );
}

export function HubNav({
  links,
  badges,
}: {
  links: HubLink[];
  /* Per-link dynamic count(s), keyed by `to`; renders a small badge per entry when > 0.
     A bare number is shorthand for a single primary-tone badge. */
  badges?: Record<string, number | HubBadge[]>;
}) {
  // Director-only links (e.g. preset templates) are hidden for everyone else.
  const user = useAuthStore((s) => s.user);
  const isDirector = user?.role === "director" || !!user?.is_ceo;
  const visible = links.filter(
    (l) => (!l.adminOnly || isDirector) && (!l.gate || l.gate(user)),
  );
  return (
    <div className="flex gap-0 border-b border-[var(--color-border)] mb-5 overflow-x-auto overflow-y-hidden">
      {visible.map((l) => {
        const raw = badges?.[l.to];
        const specs: HubBadge[] =
          raw == null ? [] : typeof raw === "number" ? [{ count: raw }] : raw;
        const shown = specs.filter((b) => b.count > 0);
        return (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            className={({ isActive }) =>
              clsx(
                "px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-wider whitespace-nowrap border-b-2 -mb-px transition-colors flex items-center gap-1.5",
                isActive
                  ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                  : "border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]",
              )
            }
          >
            {l.label}
            {shown.map((b, i) => (
              <span
                key={i}
                title={b.title}
                className={clsx(
                  "rounded-full px-1.5 py-0.5 text-[10px] leading-none",
                  b.tone === "amber"
                    ? "bg-amber-500 text-white"
                    : b.tone === "red"
                      ? "bg-red-500 text-white"
                      : "bg-[var(--color-primary)] text-[var(--color-text-inverse)]",
                )}
              >
                {b.count}
              </span>
            ))}
          </NavLink>
        );
      })}
    </div>
  );
}

// ── table shells ─────────────────────────────────────────────────────────────
// Wide tables get a horizontal scrollbar pinned to the bottom of the viewport
// (a sticky twin scroller synced with the real one), so users don't have to
// scroll to the table's end to reach it. The scroller's native bar is hidden —
// the sticky twin is the only visible one.
export function TableShell({ children, tableClassName }: { children: React.ReactNode; tableClassName?: string }) {
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const barRef = React.useRef<HTMLDivElement>(null);
  const spacerRef = React.useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = React.useState(false);

  React.useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const update = () => {
      setHasOverflow(scroller.scrollWidth > scroller.clientWidth + 1);
      if (spacerRef.current) spacerRef.current.style.width = `${scroller.scrollWidth}px`;
      if (barRef.current) barRef.current.scrollLeft = scroller.scrollLeft;
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(scroller);
    if (scroller.firstElementChild) ro.observe(scroller.firstElementChild);
    return () => ro.disconnect();
  }, []);

  // Assigning an unchanged scrollLeft fires no scroll event, so the two-way
  // sync converges without loop guards.
  const syncFromScroller = () => {
    if (barRef.current && scrollerRef.current)
      barRef.current.scrollLeft = scrollerRef.current.scrollLeft;
  };
  const syncFromBar = () => {
    if (barRef.current && scrollerRef.current)
      scrollerRef.current.scrollLeft = barRef.current.scrollLeft;
  };

  return (
    <div className="card">
      <div
        ref={scrollerRef}
        onScroll={syncFromScroller}
        className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <table className={`w-full text-xs${tableClassName ? ` ${tableClassName}` : ""}`}>{children}</table>
      </div>
      <div
        ref={barRef}
        onScroll={syncFromBar}
        className={`sticky bottom-0 z-10 overflow-x-auto overflow-y-hidden${hasOverflow ? "" : " hidden"}`}
      >
        <div ref={spacerRef} className="h-px" />
      </div>
    </div>
  );
}
export function Th({
  children,
  className,
  style,
}: {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <th
      style={style}
      className={clsx(
        "text-left px-3 py-2 font-mono text-[0.65rem] font-bold uppercase tracking-widest text-[var(--color-text-muted)] bg-[var(--color-bg-elevated)]",
        className,
      )}
    >
      {children}
    </th>
  );
}

// ── multifile attachments (shared by proceedings + register) ─────────────────
// A read-only list of already-uploaded attachments.
//
// Klik prosi API o link podpisany W TEJ CHWILI (useManagedFileOpener): `url` z payloadu
// to presigned S3 ważny godzinę od pobrania danych, a te listy wiszą na stronach
// otwartych dużo dłużej (sekwencja masowa WPW, zakładka Działki) — wtedy S3 odpowiada
// „AccessDenied — Request has expired". Sam href zostaje jako zapasowy (nowa karta,
// kopiowanie adresu) i w ciągu tej godziny działa jak dotąd.
export function AttachmentLinks({
  files,
  itemClassName,
}: {
  files: { id: string; original_filename: string; url: string | null }[];
  // Callers in width-constrained tables pass e.g. "max-w-[200px] truncate" so a long
  // filename shortens instead of stretching the column past the viewport.
  itemClassName?: string;
}) {
  const { openFile } = useManagedFileOpener();
  if (!files.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {files.map((f) =>
        f.url ? (
          <a
            key={f.id}
            href={f.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => {
              if (!isPlainLeftClick(e)) return;
              e.preventDefault();
              void openFile(f);
            }}
            title={f.original_filename}
            className={clsx(
              "inline-flex min-w-0 items-center gap-1 px-1.5 py-0.5 text-[11px] font-mono border border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-primary)] hover:underline",
              itemClassName,
            )}
          >
            <span className="truncate">{f.original_filename}</span>
          </a>
        ) : (
          <span
            key={f.id}
            title={f.original_filename}
            className={clsx(
              "inline-flex min-w-0 px-1.5 py-0.5 text-[11px] font-mono border border-[var(--color-border)] text-[var(--color-text-secondary)]",
              itemClassName,
            )}
          >
            <span className="truncate">{f.original_filename}</span>
          </span>
        ),
      )}
    </div>
  );
}

// Pojedynczy link „Pobierz" do pliku z rejestru — z tego samego powodu co AttachmentLinks
// klik prosi API o link podpisany W TEJ CHWILI (Generacja dokumentów: listy szablonów
// i przebiegów anonimizacji wiszą otwarte godzinami). Wygląd zostaje po stronie wołającego.
export function ManagedFileDownload({
  file,
  className,
  title,
  children,
}: {
  file: { id: string; original_filename: string; url: string | null };
  className?: string;
  title?: string;
  children: React.ReactNode;
}) {
  const { openFile } = useManagedFileOpener();
  if (!file.url) return null;
  return (
    <a
      href={file.url}
      download={file.original_filename}
      onClick={(e) => {
        if (!isPlainLeftClick(e)) return;
        e.preventDefault();
        void openFile(file);
      }}
      className={className}
      title={title}
    >
      {children}
    </a>
  );
}

// A controlled multi-file picker (held as File[] by the parent; appended to FormData as
// repeated `files` parts). The caller owns the state so it can reset after a successful add.
export function MultiFileInput({
  files,
  onChange,
  label = "Załączniki (wiele plików)",
}: {
  files: File[];
  onChange: (files: File[]) => void;
  label?: string;
}) {
  return (
    <div className="space-y-1">
      <span className="section-label">{label}</span>
      <FileDropZone multiple files={files} onFilesChange={onChange} />
    </div>
  );
}

// Append repeated `files` parts onto a FormData (the API multifile convention).
export function appendFiles(fd: FormData, files: File[]): void {
  for (const f of files) fd.append("files", f);
}

/**
 * Styled click-or-drag file picker — replaces the raw `<input type="file">`
 * ("Browse… No file selected") with a dashed dropzone matching the app's
 * industrial look. Fire-and-forget: hands picked/dropped files to `onFiles`
 * and clears itself (the parent renders whatever was uploaded).
 */
export function FileDrop({
  onFiles,
  accept,
  multiple = false,
  disabled = false,
  busy = false,
  label,
  hint,
  className,
}: {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  busy?: boolean;
  label?: string;
  hint?: string;
  className?: string;
}) {
  const locked = disabled || busy;
  return (
    <div className={className}>
      <FileDropZone
        onSelect={(files) => onFiles(files)}
        accept={accept}
        multiple={multiple}
        disabled={locked}
        label={busy ? "Wgrywanie…" : label}
      />
      {hint && !busy && (
        <p className="mt-0.5 text-[10px] text-[var(--color-text-muted)]">{hint}</p>
      )}
    </div>
  );
}
